import React, { useEffect, useState } from 'react';
import DashCard from "./DashCard";
import CheckSeal from "../../assets/icons/CheckSeal.svg";
import BarChart from "../../assets/icons/BarChart.svg";
import ListChecks from "../../assets/icons/ListChecks.svg";
import Buildings from "../../assets/icons/Buildings.svg";
import UserPlus from "../../assets/icons/UserPlus.svg";
import User from "../../assets/icons/User.svg";
import { useGetUserQuery, useLazyGetFirmsQuery, useLazyGetProjectDashCountsQuery, useLazyGetProjectsQuery, useLazyGetUsersQuery } from "api/api";

const DashCards = () => {
  const { data: userData } = useGetUserQuery();
  const [triggerGetUsers, { data: users }] = useLazyGetUsersQuery();
  const [triggerGetFirms, { data: firms }] = useLazyGetFirmsQuery();
  const [triggerGetProjectCounts, { data: projectCounts }] = useLazyGetProjectDashCountsQuery();
  const [cardData, setCardData] = useState([]);

  const generateFinancialYearRange = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear() - (currentMonth < 3 ? 1 : 0); // If Jan/Feb/March, use previous year
    const start = new Date(currentYear, 3, 1); // April 1st of the current year
    const end = new Date(currentYear + 1, 2, 31); // March 31st of the next year

    const format = date => `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(-2)}`;
    return `${format(start)}-${format(end)}`;
  };

  const roleCards = {
    "Firm User": [
      { title: "Firm Projects Submitted (All time)", svgIcon: BarChart, getData: () => projectCounts?.submitted_projects ?? null, redirect: "/projects?status=submitted" },
      {
        title: "My Projects (Draft & Submitted)",
        svgIcon: ListChecks, getData: () => projectCounts?.my_projects ?? null,
        redirect: `/projects?creator=${encodeURIComponent(`${userData?.first_name} ${userData?.last_name}`)}`
      },
      {
        title: `Firm Projects Submitted ${generateFinancialYearRange()}`,
        svgIcon: CheckSeal, getData: () => projectCounts?.submitted_projects_current ?? null,
        redirect: `/projects?status=submitted&date_range=${encodeURIComponent(generateFinancialYearRange())}`
      },
    ],
    "Firm Admin": [
      { title: "Firm Users", svgIcon: UserPlus, getData: () => users?.filter(u => u.site_role === "Firm User").length ?? null, redirect: "/firm-users" },
      { title: "Firm Projects Submitted (All time)", svgIcon: BarChart, getData: () => projectCounts?.submitted_projects ?? null, redirect: "/projects?status=submitted" },
      {
        title: "My Projects (Draft & Submitted)",
        svgIcon: ListChecks, getData: () => projectCounts?.my_projects ?? null,
        redirect: `/projects?creator=${encodeURIComponent(`${userData?.first_name} ${userData?.last_name}`)}`
      },
      {
        title: `Firm Projects Submitted ${generateFinancialYearRange()}`,
        svgIcon: CheckSeal, getData: () => projectCounts?.submitted_projects_current ?? null,
        redirect: `/projects?status=submitted&date_range=${encodeURIComponent(generateFinancialYearRange())}`
      },
    ],
    "Sub Super Admin": [
      { title: "Firms", svgIcon: Buildings, getData: () => firms?.length ?? null, redirect: "/firms" },
      { title: "Active Firm Admins", svgIcon: User, getData: () => users?.filter(u => u.site_role === "Firm Admin").length ?? null, redirect: "/firm-admin" },
      { title: "Active Firm Users", svgIcon: UserPlus, getData: () => users?.filter(u => u.site_role === "Firm User").length ?? null, redirect: "/firm-users" },
      { title: "Projects", svgIcon: UserPlus, getData: () => projectCounts?.submitted_projects ?? null, redirect: "/projects" }
    ],
    "Super Admin": [
      { title: "Firms", svgIcon: Buildings, getData: () => firms?.length ?? null, redirect: "/firms" },
      { title: "Active Firm Admins", svgIcon: User, getData: () => users?.filter(u => u.site_role === "Firm Admin").length ?? null, redirect: "/firm-admin" },
      { title: "Active Firm Users", svgIcon: UserPlus, getData: () => users?.filter(u => u.site_role === "Firm User").length ?? null, redirect: "/firm-users" },
      { title: "Projects", svgIcon: UserPlus, getData: () => projectCounts?.submitted_projects ?? null, redirect: "/projects" }
    ]
  };

  useEffect(() => {
    const userRole = userData?.site_role || "";
    const cardsToRender = roleCards[userRole] || [];

    triggerGetProjectCounts()
    // Determine which other queries to trigger based on user role
    if (["Sub Super Admin", "Super Admin"].includes(userRole)) {
      triggerGetFirms();
      triggerGetUsers();
    } else if (userRole === "Firm Admin") {
      triggerGetUsers();
    }

    // Update card data when queries complete
    setCardData(cardsToRender);
  }, [userData, firms, users, projectCounts]);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '24px',
      width: 'fit-content',
    }}>
      {cardData.map((card: any, index) => (
        <DashCard
          key={index}
          title={card.title}
          number={card.getData()}
          svgIcon={card.svgIcon}
          redirect={card.redirect}
        />
      ))}
    </div>
  );
};

export default DashCards;